import {
  ACTIVITY_CATEGORY_LABELS,
  ACTIVITY_HOST_KIND_LABELS,
  ACTIVITY_STATUS_LABELS,
  formatDateOnly,
  toFacilityDate,
  type ActivityCategory,
  type ActivityHostKind,
  type ActivityStatus,
  type StatusTone,
} from "@alm/domain";
import type { orpcClient, Output } from "@/lib/orpc";

/** Derived from the API rather than retyped beside it. */
export type ActivityRow = Output<typeof orpcClient.care.activityCalendar.list>["rows"][number];
export type ActivityDetail = Output<typeof orpcClient.care.activityCalendar.get>;

export const ACTIVITY_VIEWS = ["month", "week", "list"] as const;
export type ActivityView = (typeof ACTIVITY_VIEWS)[number];
export const ACTIVITY_VIEW_KEY = "activity-calendar-view";

const TONES: Record<ActivityStatus, StatusTone> = {
  planned: "neutral",
  confirmed: "info",
  completed: "ok",
  cancelled: "crit",
};

export const statusLabelOf = (status: string) =>
  ACTIVITY_STATUS_LABELS[status as ActivityStatus] ?? status;
export const categoryLabelOf = (category: string) =>
  ACTIVITY_CATEGORY_LABELS[category as ActivityCategory] ?? category;
export const hostKindLabelOf = (kind: string) =>
  ACTIVITY_HOST_KIND_LABELS[kind as ActivityHostKind] ?? kind;
export const toneOf = (status: string): StatusTone => TONES[status as ActivityStatus] ?? "neutral";

/** The day an instant falls on, read on the given clock. */
export const dayIn = (instant: string | Date, zone: string) =>
  toFacilityDate(new Date(instant), zone);

const clockFor = (instant: string | Date, zone: string) =>
  new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(instant));

/** "2:30 PM" on the given clock. */
export const clockIn = clockFor;

/** "14:30" on the given clock: what a time input holds. */
export function clockValueIn(instant: string | Date, zone: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(instant));
}

/** "Oct 10, 2026, 2:00 PM to 3:00 PM", or "Oct 10, 2026 (all day)". */
export function activityWhen(
  activity: {
    startsAt: string | Date;
    endsAt: string | Date | null;
    allDay: boolean;
  },
  zone: string,
): string {
  const day = formatDateOnly(activity.startsAt, zone);
  if (activity.allDay) return `${day} (all day)`;
  const start = clockFor(activity.startsAt, zone);
  if (!activity.endsAt) return `${day}, ${start}`;
  return `${day}, ${start} to ${clockFor(activity.endsAt, zone)}`;
}

/** Who runs it, as one line. */
export function hostOf(activity: {
  hostKind: string;
  hostName: string | null;
  partnerName: string | null;
}): string {
  if (activity.hostKind === "partner") return activity.partnerName ?? "An outside company";
  return activity.hostName ?? hostKindLabelOf(activity.hostKind);
}
